import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  ShieldCheck,
  Sparkles,
  Info,
  Loader2,
  Timer,
  PencilLine,
  RefreshCcw,
} from "lucide-react";
import { sendOtp, verifyOtp, establishSession } from "@/lib/authApi";
import { clearAuthSession, getAccessToken, isAccessTokenExpired, normalizePhone } from "@/lib/auth";
import { isBackendConfigured } from "@/lib/config";
import { useRole } from "@/store/role";
import { firstAllowedPath } from "@/config/navigation";
import { resolveEffectivePermissions } from "@/lib/permissions";
import MahindraLogo from "@/components/MahindraLogo";
import LoginBrandPanel from "@/components/login/LoginBrandPanel";
import { toast } from "sonner";

const DEFAULT_HOME = "/hr/dashboard";

const inputClass =
  "mt-1.5 flex h-12 w-full rounded-xl border border-[#E5E7EB] bg-white px-4 text-[15px] text-[#111] placeholder:text-[#9CA3AF] outline-none transition focus:border-[#B60F2A] focus:ring-2 focus:ring-[#B60F2A]/20 disabled:cursor-not-allowed disabled:bg-[#F7F7F7] disabled:text-[#6B7280]";

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setSessionUser } = useRole();
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [nameHint, setNameHint] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const otpRef = useRef(null);
  const expiredNotice = useRef(false);

  useEffect(() => {
    const token = getAccessToken();
    if (token && isAccessTokenExpired(token)) {
      clearAuthSession();
      setSessionUser(null);
    }
    if (expiredNotice.current) return;
    if (params.get("session") !== "expired") return;
    expiredNotice.current = true;
    toast.info("Your session expired. Please sign in again.");
    navigate("/login", { replace: true });
  }, [params, navigate, setSessionUser]);

  const resetOtpFlow = () => {
    setOtpSent(false);
    setOtp("");
    setNameHint("");
  };

  const requestOtp = async (e) => {
    e?.preventDefault?.();
    const phone = normalizePhone(mobile);
    if (phone.length < 10)
      return toast.error("Enter a valid 10-digit mobile number");
    setSubmitting(true);
    try {
      const result = await sendOtp(phone);
      setMobile(phone);
      setNameHint(result.name_hint || "");
      setOtp("");
      setOtpSent(true);
      toast.success(result.message || "OTP sent successfully");
      setTimeout(() => otpRef.current?.focus(), 100);
    } catch (err) {
      toast.error(err?.message || "Failed to request OTP");
    } finally {
      setSubmitting(false);
    }
  };

  const verifyOtpSubmit = async (e) => {
    e?.preventDefault?.();
    if (!otpSent) return requestOtp(e);
    if (otp.trim().length !== 6) return toast.error("Enter the 6-digit OTP");
    setSubmitting(true);
    try {
      const session = await verifyOtp(mobile, otp.trim());
      const user = await establishSession(session, mobile);
      if (user) setSessionUser(user);
      toast.success(`Welcome${user?.name ? `, ${user.name}` : ""}!`);
      const perms = resolveEffectivePermissions(user);
      navigate(firstAllowedPath(user, perms) || DEFAULT_HOME);
    } catch (err) {
      toast.error(err?.message || "Invalid OTP");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="grid min-h-screen grid-cols-1 bg-white lg:grid-cols-2"
      data-testid="login-page"
    >
      <LoginBrandPanel />

      {/* Right: form — same structure as Registration Portal */}
      <div className="flex min-h-screen flex-col">
        <div className="border-b border-[#E5E7EB] bg-white p-6 lg:hidden">
          <MahindraLogo size="compact" className="max-w-[200px]" />
        </div>

        <div className="flex flex-1 items-center justify-center px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-md"
            data-testid="login-card"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-[#FBE7EA] px-3 py-1 text-xs font-semibold text-[#B60F2A]">
              <Sparkles className="h-3.5 w-3.5" />
              MU One · Campus Portal
            </div>

            <h2 className="mt-4 text-3xl font-bold tracking-tight text-[#111] md:text-4xl">
              {otpSent ? "Verify your number" : "Sign in to MU One"}
            </h2>
            <p className="mt-2 text-[#6B7280]">
              {otpSent
                ? "Enter the 6-digit OTP sent to your mobile."
                : "Enter your registered mobile number to continue."}
            </p>

            {!otpSent && (
              <div
                className="mt-6 rounded-2xl border border-[#FBE7EA] bg-[#FBE7EA]/40 p-5"
                data-testid="mobile-notice"
              >
                <div className="flex items-start gap-3">
                  <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#B60F2A]" />
                  <p className="text-sm leading-relaxed text-[#111]">
                    Use the mobile number registered with Mahindra University to
                    receive your one-time password and access campus services.
                  </p>
                </div>
              </div>
            )}

            <form
              onSubmit={verifyOtpSubmit}
              className="mt-6 space-y-4"
              data-testid={otpSent ? "otp-step" : "phone-step"}
            >
              <div>
                <label
                  className="text-sm font-medium text-[#374151]"
                  htmlFor="login-mobile"
                >
                  Mobile Number
                </label>
                <div className="relative mt-1.5">
                  <Phone className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]" />
                  <input
                    id="login-mobile"
                    type="tel"
                    value={mobile}
                    onChange={(e) => {
                      setMobile(e.target.value);
                      if (otpSent) resetOtpFlow();
                    }}
                    placeholder="+91 98XXXXXXXX"
                    className={`${inputClass} pl-10 ${otpSent ? "bg-[#F7F7F7] text-[#6B7280]" : ""}`}
                    data-testid="login-mobile-input"
                    autoFocus={!otpSent}
                    disabled={submitting || otpSent}
                    readOnly={otpSent}
                  />
                </div>
              </div>

              <AnimatePresence mode="wait">
                {!otpSent ? (
                  <motion.div
                    key="send"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                  >
                    <button
                      type="button"
                      onClick={requestOtp}
                      disabled={
                        submitting || !mobile.trim() || !isBackendConfigured()
                      }
                      className="mt-2 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#C45B6A] text-base font-semibold text-white transition hover:bg-[#B60F2A] disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid="login-send-otp"
                    >
                      {submitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <ShieldCheck className="h-4 w-4" />
                          Send OTP
                        </>
                      )}
                    </button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="otp"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="space-y-4"
                  >
                    {nameHint && (
                      <p className="text-sm text-[#6B7280]">
                        Welcome{" "}
                        <span className="font-semibold text-[#111]">
                          {nameHint}
                        </span>
                      </p>
                    )}

                    <div>
                      <label
                        className="text-sm font-medium text-[#374151]"
                        htmlFor="login-otp"
                      >
                        6-digit OTP
                      </label>
                      <input
                        id="login-otp"
                        ref={otpRef}
                        inputMode="numeric"
                        pattern="\d{6}"
                        maxLength={6}
                        value={otp}
                        onChange={(e) =>
                          setOtp(e.target.value.replace(/\D/g, ""))
                        }
                        placeholder="••••••"
                        className={`${inputClass} text-center font-mono text-2xl tracking-[0.4em]`}
                        data-testid="login-otp-input"
                        disabled={submitting}
                      />
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-[#6B7280]">
                        <Timer className="h-3.5 w-3.5" />
                        Check your SMS for the code sent to{" "}
                        <span className="font-mono font-semibold text-[#111]">
                          {mobile}
                        </span>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting || otp.length !== 6}
                      className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#C45B6A] text-base font-semibold text-white transition hover:bg-[#B60F2A] disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid="login-verify-otp"
                    >
                      {submitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <ShieldCheck className="h-4 w-4" />
                          Verify &amp; continue
                        </>
                      )}
                    </button>

                    <div className="flex items-center justify-between gap-3 text-sm">
                      <button
                        type="button"
                        onClick={resetOtpFlow}
                        data-testid="login-back-to-phone"
                        className="inline-flex items-center gap-1.5 text-[#6B7280] transition hover:text-[#B60F2A]"
                      >
                        <PencilLine className="h-3.5 w-3.5" />
                        Change number
                      </button>
                      <button
                        type="button"
                        onClick={requestOtp}
                        disabled={submitting || !mobile.trim()}
                        className="inline-flex items-center gap-1.5 font-semibold text-[#B60F2A] hover:underline disabled:opacity-50"
                        data-testid="login-resend-otp"
                      >
                        <RefreshCcw className="h-3.5 w-3.5" />
                        Resend OTP
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>

            <p className="mt-8 text-center text-xs text-[#9CA3AF]">
              By continuing you agree to Mahindra University&apos;s Terms of
              Service and Privacy Policy.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
